"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { apiClient } from "@/lib/api-client";
import { useSidebar } from "@/components/ui/sidebar";

const POLL_INTERVAL_MS = 8000;
const RECENTLY_FINISHED_DURATION_MS = 10000;

export type SessionActivity = "running" | "recently_finished" | "idle";

/**
 * Polls batch-request-status for all visible sidebar chat IDs.
 * Returns a Map<chatId, SessionActivity> that drives the green/yellow glow.
 */
export function useActiveSessions(chatIds: string[]) {
  const { open: sidebarOpen } = useSidebar();
  const [activityMap, setActivityMap] = useState<Map<string, SessionActivity>>(
    new Map()
  );
  const previouslyRunningRef = useRef<Set<string>>(new Set());
  const recentlyFinishedTimersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );
  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const poll = useCallback(async () => {
    if (!chatIds.length) return;
    try {
      const { results } = await apiClient.batchRequestStatus(chatIds);
      const nowRunning = new Set<string>();
      const nextMap = new Map<string, SessionActivity>();

      for (const r of results) {
        if (r.status === "running") {
          nowRunning.add(r.session_id);
          nextMap.set(r.session_id, "running");
        }
      }

      // Detect transitions: was running → no longer running = recently_finished
      for (const sid of previouslyRunningRef.current) {
        if (!nowRunning.has(sid)) {
          nextMap.set(sid, "recently_finished");

          // Clear any existing timer for this session
          const existing = recentlyFinishedTimersRef.current.get(sid);
          if (existing) clearTimeout(existing);

          // After RECENTLY_FINISHED_DURATION_MS, transition to idle
          const timer = setTimeout(() => {
            recentlyFinishedTimersRef.current.delete(sid);
            setActivityMap((prev) => {
              const next = new Map(prev);
              if (next.get(sid) === "recently_finished") {
                next.delete(sid);
              }
              return next;
            });
          }, RECENTLY_FINISHED_DURATION_MS);
          recentlyFinishedTimersRef.current.set(sid, timer);
        }
      }

      // Preserve any existing recently_finished entries that haven't timed out
      setActivityMap((prev) => {
        const merged = new Map(nextMap);
        for (const [sid, activity] of prev) {
          if (activity === "recently_finished" && !merged.has(sid)) {
            merged.set(sid, "recently_finished");
          }
        }
        return merged;
      });

      previouslyRunningRef.current = nowRunning;
    } catch {
      // Silently ignore polling errors
    }
  }, [chatIds]);

  useEffect(() => {
    if (!sidebarOpen || !chatIds.length) return;

    // Initial poll
    poll();

    // Set up interval
    pollTimerRef.current = setInterval(poll, POLL_INTERVAL_MS);

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [sidebarOpen, poll, chatIds]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      for (const timer of recentlyFinishedTimersRef.current.values()) {
        clearTimeout(timer);
      }
      recentlyFinishedTimersRef.current.clear();
    };
  }, []);

  const getActivity = useCallback(
    (chatId: string): SessionActivity => activityMap.get(chatId) ?? "idle",
    [activityMap]
  );

  return { activityMap, getActivity };
}
